import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Label from "../../Common/Label";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  MenuItem,
  Select,
  Slider,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import WarningIcon from "../../../assets/images/Warning.svg";
import AiIcon from "../../../assets/images/AiIcon.svg";
import ValidationSchema from "../Validations/Form5";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Form, Formik } from "formik";
import {
  employmentTypeOptions,
  keyValueForCurrency,
  periodOptions,
} from "../../../constant/VonqConstant";
import { generateTexts } from "../../../utils/AIGenerateText";
import { ApiContext } from "../../../context/VonqApiContext";
import { getRequest } from "../../../api/apiServices";
import apiList, { getCompany } from "../../../api/apiList";
import { useAuth } from "../../../hooks/useAuth";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { companyJobList } from "../../../store/jobSlice";
import { debounce } from "lodash";
import {
  formatPostcodeInput,
  getPostcodePlaceholder,
  isValidPostcode,
  normalizePostcode,
} from "../../../utils/postcodeUtils";
import { VacancyTextVariant } from "../../../types/campaignPrefill";

interface Form5Props {
  nextStep: () => void;
  prevStep: () => void;
}

function toHourFieldValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

const Form5: React.FC<Form5Props> = ({ nextStep, prevStep }) => {
  const [logo, setLogo] = useState<File | string | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const apiContext = useContext(ApiContext);
  const { user } = useAuth();
  const [error, setError] = useState<string>("");
  const [companyInfoData, setCompanyInfoData] = useState<{
    companyName: string;
    companyLogo: string;
    companyId: number;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  }>({ companyName: "", companyLogo: "", companyId: 0 });
  const [hasSavedForm5, setHasSavedForm5] = useState<boolean>(false);
  const [vacancyVariants, setVacancyVariants] = useState<{
    professional: string;
    neutral: string;
    engaging: string;
  } | null>(null);
  const [selectedVacancyVariant, setSelectedVacancyVariant] =
    useState<VacancyTextVariant>("professional");
  const [isPrefillFlow, setIsPrefillFlow] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const jobs = useSelector((state: RootState) => state.companyJob);

  const isMobile = useMediaQuery("(max-width:578px)");
  const [initialValues, setInitialValues] = useState({
    campaignName: "",
    jobTitle: "",
    jobDescription: "",
    yearsOfExperience: 4,
    employmentType: "",
    fromWorkingHours: "",
    fromWorkingMinutes: "",
    toWorkingHours: "",
    toWorkingMinutes: "",
    salaryIndicationFrom: "",
    salaryIndicationTo: "",
    currency: "",
    period: "",
    addressLine1: "",
    addressLine2: "",
    postCode: "",
    city: "",
    country: "",
    allowsRemoteWork: true,
  });
  // Function to store Form5 data to localStorage
  const storeForm5DataToLocalStorage = (values: any) => {
    const storedCampaignData = localStorage.getItem("CompaignData");
    const existingCampaignData = storedCampaignData
      ? JSON.parse(storedCampaignData)
      : {};

    // Default salaryIndicationTo to empty string if not provided
    const salaryTo =
      values.salaryIndicationTo === undefined ||
      values.salaryIndicationTo === null
        ? ""
        : String(values.salaryIndicationTo);

    const form5Data = {
      campaignData: {
        campaignName: values.campaignName || "",
        jobTitle: values.jobTitle || "",
        jobDescription: values.jobDescription || "",
        yearsOfExperience: values.yearsOfExperience || 4,
        employmentType: values.employmentType || "",
        fromWorkingHours: values.fromWorkingHours || "",
        fromWorkingMinutes: values.fromWorkingMinutes || "",
        toWorkingHours: values.toWorkingHours || "",
        toWorkingMinutes: values.toWorkingMinutes || "",
        salaryIndicationFrom: values.salaryIndicationFrom || "",
        salaryIndicationTo: salaryTo,
        currency: values.currency || "",
        period: values.period || "",
      },
      workingLocation: {
        addressLine1: values.addressLine1 || "",
        addressLine2: values.addressLine2 || "",
        postCode: values.postCode || "",
        city: values.city || "",
        country: values.country || "",
        allowsRemoteWork:
          typeof values.allowsRemoteWork === "boolean"
            ? values.allowsRemoteWork
            : true,
      },
    };

    const updatedCampaignData = {
      ...existingCampaignData,
      Form5Data: form5Data,
      vacancyTextVariant: selectedVacancyVariant,
    };

    localStorage.setItem("CompaignData", JSON.stringify(updatedCampaignData));
  };

  // Load data from localStorage on component mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem("CompaignData");
      const parsed: any =
        stored && stored !== "undefined" && stored !== "null"
          ? JSON.parse(stored)
          : {};
      const form5: any = parsed?.Form5Data || {};
      const form1: any = parsed?.Form1Data || {};
      const ctx: any = apiContext?.selectedValues || {};

      const campaingdata = form5?.campaignData || {};
      const workinglocationdata = form5?.workingLocation || {};

      setHasSavedForm5(Boolean(parsed?.Form5Data));
      setIsPrefillFlow(Boolean(parsed?._prefillLoaded));

      if (parsed?.vacancyTextVariants) {
        setVacancyVariants(parsed.vacancyTextVariants);
      }
      if (parsed?.vacancyTextVariant) {
        setSelectedVacancyVariant(parsed.vacancyTextVariant);
      }

      const defaultJobTitle =
        form1?.jobTitle?.name ||
        campaingdata?.jobTitle ||
        ctx?.jobTitle ||
        "";
      setInitialValues({
        campaignName: campaingdata?.campaignName || "",
        jobTitle: defaultJobTitle,
        jobDescription: campaingdata?.jobDescription || "",
        yearsOfExperience: campaingdata?.yearsOfExperience ?? 4,
        employmentType: campaingdata?.employmentType || "",
        fromWorkingHours: toHourFieldValue(campaingdata?.fromWorkingHours),
        fromWorkingMinutes: toHourFieldValue(campaingdata?.fromWorkingMinutes ?? "0"),
        toWorkingHours: toHourFieldValue(campaingdata?.toWorkingHours),
        toWorkingMinutes: toHourFieldValue(campaingdata?.toWorkingMinutes ?? "0"),
        salaryIndicationFrom: campaingdata?.salaryIndicationFrom || "",
        // Clear salaryIndicationTo if it's "0", empty, or if it looks like an unwanted default value
        // Only keep it if it's a valid user-entered value
        salaryIndicationTo:
          campaingdata?.salaryIndicationTo &&
          campaingdata.salaryIndicationTo !== "0" &&
          campaingdata.salaryIndicationTo !== "" &&
          campaingdata.salaryIndicationTo !== null &&
          campaingdata.salaryIndicationTo !== undefined
            ? campaingdata.salaryIndicationTo
            : "",
        currency: campaingdata?.currency || "",
        period: campaingdata?.period || "",
        addressLine1: workinglocationdata?.addressLine1 || "",
        addressLine2: workinglocationdata?.addressLine2 || "",
        postCode: workinglocationdata?.postCode || "",
        city: workinglocationdata?.city || "",
        country: workinglocationdata?.country || "",
        allowsRemoteWork:
          typeof workinglocationdata?.allowsRemoteWork === "boolean"
            ? workinglocationdata?.allowsRemoteWork
            : true,
      });
    } catch {}
  }, [apiContext]);

  // Fetch company information like Form6 to provide accurate company name
  const fetchCompany = async () => {
    try {
      const response: any = await getRequest(getCompany(user?.uId));
      if (response?.data?.data) {
        const data = response.data.data || {};
        setCompanyInfoData({
          companyName: data.name,
          companyLogo: data.logo,
          companyId: data.id,
          city: data.city || data?.companyInfo?.city || "",
          state: data.state || data?.companyInfo?.state || "",
          country: data.country || data?.companyInfo?.country || "",
          zipCode: data.zipCode || data?.companyInfo?.zipCode || "",
        });
      }
      setInitialValues((prev) => ({
        ...prev,
        addressLine1: prev.addressLine1 || response.data.data.name,
      }));
    } catch (error) {
      setError("Failed to Fetch Company Info" + error);
    }
  };

  useEffect(() => {
    if (user?.uId) fetchCompany();
  }, [user?.uId]);

  useEffect(() => {
    dispatch(companyJobList());
  }, [dispatch]);

  const parseIdealExperienceLevel = (text: any): number | undefined => {
    const raw = String(text || "");
    const matches = raw.match(/\d+(?:\.\d+)?/g);
    if (!matches || matches.length === 0) return undefined;
    if (matches.length >= 2) {
      const a = parseFloat(matches[0]);
      const b = parseFloat(matches[1]);
      return Math.round((a + b) / 2);
    }
    return Math.round(parseFloat(matches[0]));
  };

  const parseSalaryRange = (
    salaryText: any
  ): { from: string; to: string } | null => {
    const raw = String(salaryText || "");
    if (!raw) return null;
    const numbers = raw.match(/\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length === 0) return null;
    if (numbers.length === 1) {
      return { from: "", to: String(Number(numbers[0])) };
    }
    const firstValue = Number(numbers[0]);
    const secondValue = Number(numbers[1]);
    const from = Math.min(firstValue, secondValue);
    const to = Math.max(firstValue, secondValue);
    return { from: String(from), to: String(to) };
  };

  //function for Maps a raw contract type into a normalized employment type value.
  const mapEmploymentFromContract = (contractType: any): string => {
    const v = String(contractType || "")
      .trim()
      .toLowerCase();
    if (!v) return "";

    // find the option whose label matches the input (case-insensitive)
    const match = employmentTypeOptions.find(
      (opt) => opt.label.toLowerCase() === v || opt.value.toLowerCase() === v
    );

    return match ? match.value : "";
  };

  // Prefill form  from the first job in the jobs list
  const prefillFromJobs = (jobsList: any[]) => {
    try {
      if (hasSavedForm5) return;
      const list: any[] = (jobsList as any) || [];
      if (!list.length) return;

      const firstJob: any = list[0];
      if (!firstJob) return;

      const parts = String(firstJob.workingLocation || "")
        .split(",")
        .map((p: string) => p.trim());
      const city = parts[0] || "";
      const stateOrLine2 = parts[1] || "";
      const country = parts[2] || "";
      const postCode = parts[3] || "";

      const parsedYears = parseIdealExperienceLevel(
        firstJob?.idealExperienceLevel
      );

      setInitialValues((prev) => ({
        ...prev,
        jobTitle: prev.jobTitle || firstJob.jobTitle || prev.jobTitle,
        campaignName: prev.campaignName || "",
        salaryIndicationFrom: prev.salaryIndicationFrom || "",
        salaryIndicationTo: prev.salaryIndicationTo || "",
        addressLine2: prev.addressLine2 || stateOrLine2 || prev.addressLine2,
        postCode: prev.postCode || postCode || prev.postCode,
        city: prev.city || city || prev.city,
        country: prev.country || country || prev.country,
        yearsOfExperience:
          typeof parsedYears === "number"
            ? parsedYears
            : prev.yearsOfExperience,
        employmentType:
          prev.employmentType ||
          mapEmploymentFromContract(firstJob?.contractType) ||
          "",
      }));
    } catch {}
  };

  useEffect(() => {
    if (isPrefillFlow || hasSavedForm5) return;
    prefillFromJobs((jobs as any) || []);
  }, [jobs, hasSavedForm5, isPrefillFlow]);

  // Utility function to update salary range based on job title
  const updateSalaryRange = (
    title: string,
    jobs: any[],
    setInitialValues: React.Dispatch<React.SetStateAction<any>>
  ) => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle || !jobs.length) return;

    const matchedJob = jobs.find(
      (job: any) =>
        (job?.jobTitle || "").toLowerCase() === trimmedTitle.toLowerCase()
    );

    // No match → clear salary values
    if (!matchedJob) {
      setInitialValues((prev: any) => ({
        ...prev,
        salaryIndicationFrom: "",
        salaryIndicationTo: "",
      }));
      return;
    }

    // Parse salary from job if available
    const parsedRange = parseSalaryRange(matchedJob?.salaryRange);
    setInitialValues((prev: any) => {
      // Only update if the user hasn't already set a value
      const hasExistingValue =
        prev.salaryIndicationTo &&
        prev.salaryIndicationTo !== "" &&
        prev.salaryIndicationTo !== "0";
      return {
        ...prev,
        salaryIndicationFrom: parsedRange
          ? "0"
          : prev.salaryIndicationFrom || "",
        // Don't auto-fill "To" value - let user enter it manually to avoid unwanted defaults like "44"
        // Only set if user hasn't entered anything and we have a valid parsed range
        salaryIndicationTo: hasExistingValue ? prev.salaryIndicationTo : "",
      };
    });
  };

  const title = String(initialValues?.jobTitle || "");
  useEffect(() => {
    if (isPrefillFlow || hasSavedForm5) return;
    try {
      updateSalaryRange(title, jobs, setInitialValues);
    } catch (error) {
      setError("Error while updating salary range:" + error);
    }
  }, [initialValues.jobTitle, jobs, isPrefillFlow, hasSavedForm5]);

  const handleVacancyVariantChange = (
    variant: VacancyTextVariant,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setSelectedVacancyVariant(variant);
    const description = vacancyVariants?.[variant] || "";
    setFieldValue("jobDescription", description);

    try {
      const stored = localStorage.getItem("CompaignData");
      const parsed = stored ? JSON.parse(stored) : {};
      localStorage.setItem(
        "CompaignData",
        JSON.stringify({
          ...parsed,
          vacancyTextVariant: variant,
        })
      );
    } catch {
      // ignore storage errors
    }
  };

  // Helper to prefill when user selects a job title from dropdown
  const applyJobPrefill = (
    title: string | null,
    setFieldValueFn: (field: string, value: any) => void
  ) => {
    const list: any[] = (jobs as any) || [];
    if (!title || !list.length) return;
    const found = list.find(
      (j: any) =>
        (j?.jobTitle || "").toLowerCase() === String(title).toLowerCase()
    );
    const job = found;
    if (!job) return;

    const salaryParsed = parseSalaryRange(job?.salaryRange);
    const parts = String(job.workingLocation || "")
      .split(",")
      .map((p: string) => p.trim());
    const city = parts[0] || "";
    const stateOrLine2 = parts[1] || "";
    const country = parts[2] || "";
    const postCode = parts[3] || "";

    // Auto-set salary fields: start from 0 if API salary available; else keep blank
    if (salaryParsed) {
      setFieldValueFn("salaryIndicationFrom", "0");
      // Don't auto-fill "To" value - leave it empty for user to enter manually
      // This prevents unwanted default values like "44" from being displayed
      setFieldValueFn("salaryIndicationTo", "");
    }
    setFieldValueFn("addressLine2", stateOrLine2);
    setFieldValueFn("postCode", postCode);
    setFieldValueFn("city", city);
    setFieldValueFn("country", country);

    const parsedYears = parseIdealExperienceLevel(job?.idealExperienceLevel);
    if (typeof parsedYears === "number") {
      setFieldValueFn("yearsOfExperience", parsedYears);
    }

    const mapped = mapEmploymentFromContract(job?.contractType);
    setFieldValueFn("employmentType", mapped || "");
  };

  const buildAIDescriptionPrompt = (currentValues: any) => {
    const stored = localStorage.getItem("CompaignData");
    let previous = {} as any;
    try {
      previous = stored ? JSON.parse(stored) : {};
    } catch {}

    const form1 = previous?.Form1Data || previous?.form1 || {};
    const form2 = previous?.Form2Data || previous?.form2 || {};
    const form3 = previous?.Form3Data || previous?.form3 || {};
    const form4 = previous?.Form4Data || previous?.form4 || {};
    const form5 = {
      ...previous?.Form5Data?.campaignData,
      ...(currentValues || {}),
    };

    // Helper to normalize text fields that may be objects like { id, name }
    const getText = (val: any): string => {
      if (!val) return "";
      if (typeof val === "string") return val;
      if (typeof val === "number") return String(val);
      if (typeof val === "object") {
        return (
          val?.name ||
          val?.label ||
          val?.title ||
          val?.value ||
          ""
        ).toString();
      }
      return "";
    };

    // Map required fields for the prompt
    const companyName = getText(
      companyInfoData?.companyName ||
        previous?.Form4Data?.recruiterFullName ||
        form1?.companyName ||
        form1?.organizationName ||
        user?.name ||
        ""
    );
    const jobTitleFromContext = getText(apiContext?.selectedValues?.jobTitle);
    const jobTitle = getText(
      currentValues?.jobTitle ||
        form5?.jobTitle ||
        form1?.jobTitle ||
        jobTitleFromContext
    );
    const campaignName =
      currentValues?.campaignName || form5?.campaignName || "";

    const education = getText(
      form3?.education ||
        previous?.Form3Data?.education ||
        previous?.Form3Data?.educationLevel ||
        ""
    );
    const seniority = getText(
      form3?.seniority || previous?.Form3Data?.seniority || ""
    );
    const jobFunction = getText(
      form3?.jobFunction ||
        previous?.Form3Data?.jobFunction ||
        apiContext?.selectedValues?.jobFunction
    );

    // Industry may be single object or array from context or forms
    const industriesSource: any =
      apiContext?.selectedValues?.industries ??
      form3?.industries ??
      previous?.Form3Data?.industries ??
      form2?.industries ??
      previous?.Form2Data?.industries ??
      form3?.industry ??
      previous?.Form3Data?.industry ??
      "";
    const industry = Array.isArray(industriesSource)
      ? industriesSource
          .map((i: any) => getText(i))
          .filter(Boolean)
          .join(", ")
      : getText(industriesSource);
    const keySkills = (form2?.skills || form3?.skills || []) as any[];
    const normalizedSkills = Array.isArray(keySkills)
      ? keySkills
          .map((s: any) =>
            typeof s === "string" ? s : s?.name || s?.label || ""
          )
          .filter(Boolean)
      : [];

    const responsibilitiesRaw = (form4?.responsibilities ||
      form4?.jobResponsibilities ||
      []) as any[];
    const responsibilities = Array.isArray(responsibilitiesRaw)
      ? responsibilitiesRaw
          .map((r: any) =>
            typeof r === "string" ? r : r?.name || r?.label || ""
          )
          .filter(Boolean)
      : [];

    const requirementsRaw = (form4?.requirements ||
      form4?.jobRequirements ||
      []) as any[];
    const requirements = Array.isArray(requirementsRaw)
      ? requirementsRaw
          .map((r: any) =>
            typeof r === "string" ? r : r?.name || r?.label || ""
          )
          .filter(Boolean)
      : [];

    const prompt = `You are an expert recruitment copywriter. Write a compelling and professional campaign job description using the details below. 
Make it attractive, clear, and suitable for job boards. Highlight responsibilities, requirements, and why a candidate should apply. 
Keep the tone professional but engaging.

Details:
- Education: ${education}
- Seniority: ${seniority}
- Job Function: ${jobFunction}
- Industry: ${industry}
- Campaign Name: ${campaignName}
- Company Name: ${companyName}
- Job Title: ${jobTitle}

Output:
A well-structured campaign job description that includes:
1. An engaging opening introducing the role and company.
2. Key responsibilities of the role.
3. Required qualifications and skills (based on education, seniority, job function, and industry).
4. Benefits of working with the company or in this role.
5. A short motivating closing line encouraging candidates to apply.

Formatting rules:
- Output valid HTML using only tags: <p>, <strong>, <em>, <ul>, <ol>, <li>, <br>.
- Do not include a title, only the description body in HTML.`;

    return prompt;
  };

  const handleFillWithAI = async (
    currentValues: any,
    setFieldValueFn: (field: string, value: any) => void
  ) => {
    try {
      setAiLoading(true);
      const prompt = buildAIDescriptionPrompt(currentValues);
      const description = await generateTexts(prompt);
      if (description) {
        setFieldValueFn("jobDescription", description);
      }
    } catch (e) {
    } finally {
      setAiLoading(false);
    }
  };

  // Persist Formik values to localStorage
  const FormikPersist: React.FC<{ values: any }> = ({ values }) => {
    const didMountRef = useRef(false);
    const debouncedSaveToLocalStorage = useMemo(
      () =>
        debounce((formValues: any) => {
          try {
            storeForm5DataToLocalStorage(formValues);
          } catch (error) {
            console.error("Failed to save form data", error);
          }
        }, 250),
      []
    );

    useEffect(() => {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }
      debouncedSaveToLocalStorage(values);
      return () => {
        debouncedSaveToLocalStorage.cancel();
      };
    }, [values, debouncedSaveToLocalStorage]);

    return null;
  };

  return (
    <div className="container" id="campaign-container">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={(values) => {
          storeForm5DataToLocalStorage(values);
          nextStep();
        }}
      >
        {({
          values,
          errors,
          setFieldValue,
          setFieldTouched,
          touched,
          handleSubmit,
        }) => (
          <>
            <FormikPersist values={values} />
            <Form onSubmit={handleSubmit}>
              <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
                <div className="d-flex">
                  <Avatar className="avatar">5</Avatar>
                  <Label
                    children="Posting Details"
                    fontSize={"20.94px"}
                    fontWeight={700}
                    color="#273848"
                  />
                </div>
                <Label
                  children="Posting details represents the Job Posting information that's going to be published in the products selected in this request."
                  fontWeight={500}
                  fontSize={"12.94px"}
                  color="#273848"
                />
              </div>

              {/* Campaign Name */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Campaign Name"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#8C8C8C",
                      fontWeight: 700,
                    }}
                  >
                    (Optional)
                  </span>
                </div>
                <div>
                  <Label
                    children='Campaign Name is used to easily distinguish jobs in list of campaigns. For example, campaign name could be "Software Development Manager" while Job Title could be "Want to lead a Team of Software Developers? Join us". This is only shown to you and not to the candidates'
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <TextField
                    placeholder="Type Campaign Name"
                    variant="outlined"
                    size="small"
                    name="campaignName"
                    value={values.campaignName || ""}
                    onChange={(e) =>
                      setFieldValue("campaignName", e.target.value)
                    }
                    error={touched.campaignName && Boolean(errors.campaignName)}
                    helperText={
                      errors.campaignName && touched.campaignName
                        ? errors.campaignName
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>

              {/* Job Title */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Job Title"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <Autocomplete
                  options={(jobs || [])
                    .map((j: any) => j?.jobTitle)
                    .filter(Boolean)}
                  className="w-100"
                  value={values.jobTitle || ""}
                  onChange={(event, newValue) => {
                    const title =
                      typeof newValue === "string"
                        ? newValue
                        : String(newValue || "");
                    setFieldValue("jobTitle", title);
                    applyJobPrefill(title, setFieldValue);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      const typed = newInputValue || "";
                      setFieldValue("jobTitle", typed);
                      try {
                        const list: any[] = (jobs as any) || [];
                        const match = list.find(
                          (j: any) =>
                            (j?.jobTitle || "").toLowerCase() ===
                            String(typed).toLowerCase()
                        );
                        if (!match) {
                          setFieldValue("salaryIndicationFrom", "");
                          setFieldValue("salaryIndicationTo", "");
                          setFieldValue(
                            "addressLine2",
                            companyInfoData?.state || ""
                          );
                          setFieldValue(
                            "postCode",
                            companyInfoData?.zipCode || ""
                          );
                          setFieldValue("city", companyInfoData?.city || "");
                          setFieldValue(
                            "country",
                            companyInfoData?.country || ""
                          );
                          setFieldValue("yearsOfExperience", 4);
                          setFieldValue("employmentType", "");
                        }
                      } catch (error) {
                        setError("Error while matching job title:" + error);
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type or select a job title"
                      variant="outlined"
                      size="small"
                      name="jobTitle"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: <>{params.InputProps.endAdornment}</>,
                      }}
                      error={touched.jobTitle && Boolean(errors.jobTitle)}
                      helperText={
                        errors.jobTitle && touched.jobTitle
                          ? errors.jobTitle
                          : ""
                      }
                      fullWidth
                    />
                  )}
                />
              </div>

              {/* Job Description */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Description"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                {vacancyVariants && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Select a vacancy text from your job specification
                    </Typography>
                    <Tabs
                      value={selectedVacancyVariant}
                      onChange={(_, value) =>
                        handleVacancyVariantChange(
                          value as VacancyTextVariant,
                          setFieldValue
                        )
                      }
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        mb: 1,
                        "& .MuiTab-root": { textTransform: "none" },
                      }}
                    >
                      <Tab value="professional" label="Professional" />
                      <Tab value="neutral" label="Neutral" />
                      <Tab value="engaging" label="Engaging" />
                    </Tabs>
                  </Box>
                )}
                <ReactQuill
                  value={values.jobDescription}
                  onChange={(newValue) => {
                    setFieldValue("jobDescription", newValue);
                    if (!touched.jobDescription) {
                      setFieldTouched("jobDescription", true, false);
                    }
                  }}
                  onBlur={() => setFieldTouched("jobDescription", true, false)}
                  className="text-editors"
                  style={{
                    borderRadius: "20px",
                    width: "100%",
                  }}
                />
                {errors.jobDescription && touched.jobDescription && (
                  <div
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      marginLeft: "4px",
                    }}
                  >
                    {errors.jobDescription}
                  </div>
                )}
                <div className="d-flex align-items-center gap-2 mt-2">
                  <img src={WarningIcon} alt="WarningIcon" />
                  <span
                    style={{
                      fontWeight: 500,
                      fontSize: "14px",
                      color: "#4E4E4E",
                    }}
                  >
                    Allowed HTML tags:
                    a[href|target],em,b,br,strong,i,li,ol,p,ul
                  </span>
                </div>

                {!vacancyVariants && (
                  <button
                    type="button"
                    className="ai-button mt-2"
                    onClick={() => handleFillWithAI(values, setFieldValue)}
                    disabled={aiLoading}
                    style={{
                      cursor: aiLoading ? "not-allowed" : "pointer",
                      border: "none",
                    }}
                  >
                    <img src={AiIcon} alt="AiIcon" />
                    {aiLoading
                      ? "Generating campaign description... "
                      : "Fill in with AI"}
                  </button>
                )}
              </div>

              {/* Years of Experience Slider */}
              <div className="col mt-5">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Label
                    children="Years of Experience"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <div className="d-flex align-items-center justify-content-between gap-4">
                  <Label
                    children="0"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <Slider
                    aria-label="Years of Experience"
                    value={values.yearsOfExperience}
                    onChange={(e, newValue) =>
                      setFieldValue("yearsOfExperience", newValue)
                    }
                    valueLabelDisplay="auto"
                    shiftStep={5}
                    step={1}
                    min={0}
                    max={40}
                    sx={{
                      color: "#66CCE7",
                      "& .MuiSlider-valueLabel": {
                        backgroundColor: "transparent",
                        color: "black",
                        fontSize: "14px",
                        top: -6,
                        "& *": {
                          background: "transparent",
                          color: "black",
                        },
                      },
                    }}
                  />
                  <Label
                    children="40"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                </div>
                {touched.yearsOfExperience && errors.yearsOfExperience && (
                  <div style={{ color: "red", fontSize: "13px" }}>
                    {errors.yearsOfExperience}
                  </div>
                )}
              </div>

              {/* Employment Type */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Employment Type"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                    className="mb-1"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                <Autocomplete
                  options={employmentTypeOptions}
                  className="w-100"
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  value={
                    employmentTypeOptions.find(
                      (opt) => opt.value === values.employmentType
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    setFieldValue("employmentType", newValue?.value || "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select an option"
                      variant="outlined"
                      size="small"
                      name="employmentType"
                      error={
                        touched.employmentType && Boolean(errors.employmentType)
                      }
                      helperText={
                        errors.employmentType && touched.employmentType
                          ? errors.employmentType
                          : ""
                      }
                      fullWidth
                    />
                  )}
                />
              </div>

              {/* Weekly Working Hours */}
              <div className="col mt-4">
                <Label
                  children="Weekly Working Hours"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                />
                <div className="row g-0 gap-4 gap-sm-2 flex-column flex-xl-row mt-1 weekly-hours-no-spinners">
                  <div className="col">
                    <Label
                      children="From"
                      fontWeight={500}
                      fontSize={"14.94px"}
                      color="#273848"
                    />
                    <div className="d-flex flex-column flex-md-row gap-2">
                      <TextField
                        placeholder="Enter a value for the hour"
                        variant="outlined"
                        size="small"
                        inputProps={{ max: 168, min: 0 }}
                        type="number"
                        name="fromWorkingHours"
                        value={values.fromWorkingHours ?? ""}
                        onChange={(e) => {
                          setFieldValue("fromWorkingHours", e.target.value);
                          setFieldTouched("fromWorkingHours", true, false);
                        }}
                        error={
                          touched.fromWorkingHours &&
                          Boolean(errors.fromWorkingHours)
                        }
                        helperText={
                          errors.fromWorkingHours && touched.fromWorkingHours
                            ? errors.fromWorkingHours
                            : ""
                        }
                        fullWidth
                      />
                      <TextField
                        placeholder="Enter a value for the minutes"
                        variant="outlined"
                        size="small"
                        inputProps={{ max: 60, min: 0 }}
                        type="number"
                        name="fromWorkingMinutes"
                        value={values.fromWorkingMinutes ?? ""}
                        onChange={(e) => {
                          setFieldValue("fromWorkingMinutes", e.target.value);
                          setFieldTouched("fromWorkingMinutes", true, false);
                        }}
                        error={
                          touched.fromWorkingMinutes &&
                          Boolean(errors.fromWorkingMinutes)
                        }
                        helperText={
                          errors.fromWorkingMinutes &&
                          touched.fromWorkingMinutes
                            ? errors.fromWorkingMinutes
                            : ""
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                  <div className="col">
                    <Label
                      children="To"
                      fontWeight={500}
                      fontSize={"14.94px"}
                      color="#273848"
                    />
                    {/* <div className="d-flex align-items-center justify-content-between flex-column flex-md-row  gap-2"> */}
                    <div className="d-flex justify-content-between flex-column flex-md-row  gap-2">
                      <div className="w-100">
                        <TextField
                          placeholder="Enter a value for the hour"
                          variant="outlined"
                          size="small"
                          type="number"
                          name="toWorkingHours"
                          inputProps={{ max: 168, min: 0 }}
                          value={values.toWorkingHours ?? ""}
                          onChange={(e) => {
                            setFieldValue("toWorkingHours", e.target.value);
                            setFieldTouched("toWorkingHours", true, false);
                          }}
                          error={
                            touched.toWorkingHours &&
                            Boolean(errors.toWorkingHours)
                          }
                          helperText={
                            errors.toWorkingHours && touched.toWorkingHours
                              ? errors.toWorkingHours
                              : ""
                          }
                          fullWidth
                        />
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <img src={WarningIcon} alt="WarningIcon" />
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: "14px",
                              color: "#4E4E4E",
                            }}
                          >
                            Leave it blank for the default of ( 1 )
                          </span>
                        </div>
                      </div>
                      <div className="w-100">
                        <TextField
                          placeholder="Enter a value for the minutes"
                          variant="outlined"
                          size="small"
                          type="number"
                          inputProps={{ max: 60, min: 0 }}
                          name="toWorkingMinutes"
                          value={values.toWorkingMinutes ?? ""}
                          onChange={(e) => {
                            setFieldValue("toWorkingMinutes", e.target.value);
                            setFieldTouched("toWorkingMinutes", true, false);
                          }}
                          error={
                            touched.toWorkingMinutes &&
                            Boolean(errors.toWorkingMinutes)
                          }
                          helperText={
                            errors.toWorkingMinutes && touched.toWorkingMinutes
                              ? errors.toWorkingMinutes
                              : ""
                          }
                          fullWidth
                        />
                        <div className="d-flex align-items-center gap-2 mt-2">
                          <img src={WarningIcon} alt="WarningIcon" />
                          <span
                            style={{
                              fontWeight: 500,
                              fontSize: "14px",
                              color: "#4E4E4E",
                            }}
                          >
                            Leave it blank for the default of ( 0 )
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Salary Indication */}
              <div className="col mt-4 salary-no-spinners">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Salary Indication"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                </div>
                <div className="d-flex flex-column flex-md-row gap-3 mt-1">
                  <div className="w-100">
                    <Label
                      children="From"
                      color="#273848"
                      fontWeight={700}
                      fontSize={"14.94px"}
                    />
                    <TextField
                      placeholder=">=0"
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      name="salaryIndicationFrom"
                      value={values.salaryIndicationFrom}
                      inputMode="numeric"
                      onChange={(e) => {
                        setFieldValue("salaryIndicationFrom", e.target.value);
                        setFieldTouched("salaryIndicationFrom", true, false);
                      }}
                      disabled={false}
                      error={
                        touched.salaryIndicationFrom &&
                        Boolean(errors.salaryIndicationFrom)
                      }
                      helperText={
                        errors.salaryIndicationFrom &&
                        touched.salaryIndicationFrom
                          ? errors.salaryIndicationFrom
                          : ""
                      }
                      fullWidth
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <img src={WarningIcon} alt="WarningIcon" />
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#4E4E4E",
                        }}
                      >
                        Leave it blank for the default of ( 0 )
                      </span>
                    </div>
                  </div>
                  <div className="w-100">
                    <Label
                      children="To"
                      color="#273848"
                      fontWeight={700}
                      fontSize={"14.94px"}
                    />
                    <TextField
                      placeholder=">=0"
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      name="salaryIndicationTo"
                      value={
                        String(values.salaryIndicationTo || "") === "0"
                          ? ""
                          : values.salaryIndicationTo || ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        // If user clears the field or enters "0", set to empty string (will default to 0 on submit)
                        setFieldValue(
                          "salaryIndicationTo",
                          value === "" || value === "0" ? "" : value
                        );
                        setFieldTouched("salaryIndicationTo", true, false);
                      }}
                      error={
                        touched.salaryIndicationTo &&
                        Boolean(errors.salaryIndicationTo)
                      }
                      helperText={
                        errors.salaryIndicationTo && touched.salaryIndicationTo
                          ? errors.salaryIndicationTo
                          : ""
                      }
                      fullWidth
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <img src={WarningIcon} alt="WarningIcon" />
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: "14px",
                          color: "#4E4E4E",
                        }}
                      >
                        Leave it blank for the default of ( 0 )
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Currency and Period */}
              <div className="col mt-4">
                <div className="d-flex flex-column flex-sm-row gap-3 mt-1">
                  <div className="w-100">
                    <div className="d-flex align-items-center gap-1">
                      <Label
                        children="Currency"
                        fontWeight={700}
                        fontSize={"14.94px"}
                        color="#273848"
                      />
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#8C8C8C",
                          fontWeight: 700,
                        }}
                      >
                        (Optional)
                      </span>
                    </div>
                    <Autocomplete
                      // options={currency}
                      options={Object.keys(keyValueForCurrency)}
                      className="w-100"
                      value={values.currency}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setFieldValue(
                            "currency",
                            keyValueForCurrency[
                              newValue as keyof typeof keyValueForCurrency
                            ]
                          );
                        } else {
                          setFieldValue("currency", "");
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select an option"
                          variant="outlined"
                          size="small"
                          name="currency"
                          error={touched.currency && Boolean(errors.currency)}
                          helperText={
                            errors.currency && touched.currency
                              ? errors.currency
                              : ""
                          }
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="w-100">
                    <div className="d-flex align-items-center gap-1">
                      <Label
                        children="Period"
                        fontWeight={700}
                        fontSize={"14.94px"}
                        color="#273848"
                      />
                    </div>
                    <Autocomplete
                      // options={["yearly", "hourly", "monthly"]}
                      options={periodOptions}
                      className="w-100"
                      // value={values.period}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      value={
                        periodOptions.find(
                          (opt) => opt.value === values.period
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue("period", newValue?.value || "");
                        setFieldTouched("period", true, false);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select an period"
                          variant="outlined"
                          size="small"
                          name="period"
                          error={touched.period && Boolean(errors.period)}
                          helperText={
                            errors.period && touched.period ? errors.period : ""
                          }
                          fullWidth
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Working Location Section */}
              <div className="col d-flex align-items-start justify-content-center flex-column gap-1 mt-5">
                <div className="d-flex">
                  <Avatar className="avatar">7</Avatar>
                  <Label
                    children="Working Location"
                    fontSize={"20.94px"}
                    fontWeight={700}
                    color="#273848"
                  />
                </div>
                <Label
                  children="Fill in details for the location of the work."
                  fontWeight={500}
                  fontSize={"12.94px"}
                  color="#273848"
                />
              </div>

              {/* Address Line 1 */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <div className="d-flex align-items-center gap-1">
                    <Label
                      children="Address Line 1"
                      fontWeight={700}
                      fontSize={"14.94px"}
                      color="#273848"
                    />
                    <Label
                      children="*"
                      fontWeight={500}
                      fontSize={"14.94px"}
                      color="#FF0000"
                    />
                  </div>
                </div>
                <div>
                  <TextField
                    placeholder="Enter First Line of Address"
                    variant="outlined"
                    size="small"
                    name="addressLine1"
                    value={values.addressLine1}
                    onChange={(e) =>
                      setFieldValue("addressLine1", e.target.value)
                    }
                    error={touched.addressLine1 && Boolean(errors.addressLine1)}
                    helperText={
                      errors.addressLine1 && touched.addressLine1
                        ? errors.addressLine1
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>

              {/* Address Line 2 */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <Label
                    children="Address Line 2"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#8C8C8C",
                      fontWeight: 700,
                    }}
                  >
                    (Optional)
                  </span>
                </div>
                <div>
                  <TextField
                    placeholder="Enter Second Line of Address"
                    variant="outlined"
                    size="small"
                    name="addressLine2"
                    value={values.addressLine2}
                    onChange={(e) =>
                      setFieldValue("addressLine2", e.target.value)
                    }
                    error={touched.addressLine2 && Boolean(errors.addressLine2)}
                    helperText={
                      errors.addressLine2 && touched.addressLine2
                        ? errors.addressLine2
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>

              {/* Postcode */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Potcode"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                </div>
                <div>
                  <TextField
                    placeholder={getPostcodePlaceholder(values.country)}
                    variant="outlined"
                    size="small"
                    name="postCode"
                    value={values.postCode}
                    onChange={(e) => {
                      setFieldValue(
                        "postCode",
                        formatPostcodeInput(e.target.value, values.country)
                      );
                    }}
                    onBlur={() => {
                      if (isValidPostcode(values.postCode, values.country)) {
                        setFieldValue(
                          "postCode",
                          normalizePostcode(values.postCode, values.country)
                        );
                      }
                    }}
                    inputProps={{
                      maxLength: 12,
                    }}
                    error={touched.postCode && Boolean(errors.postCode)}
                    helperText={
                      errors.postCode && touched.postCode ? errors.postCode : ""
                    }
                    fullWidth
                  />
                </div>
              </div>

              {/* City */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="City"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                </div>
                <div>
                  <TextField
                    placeholder="Enter City of Address"
                    variant="outlined"
                    size="small"
                    name="city"
                    value={values.city}
                    onChange={(e) => setFieldValue("city", e.target.value)}
                    error={touched.city && Boolean(errors.city)}
                    helperText={errors.city && touched.city ? errors.city : ""}
                    fullWidth
                  />
                </div>
              </div>

              {/* Country */}
              <div className="col mt-4">
                <div className="d-flex align-items-center gap-1 mb-1">
                <div className="d-flex align-items-center gap-1">
                  <Label
                    children="Country"
                    fontWeight={700}
                    fontSize={"14.94px"}
                    color="#273848"
                  />
                  <Label
                    children="*"
                    fontWeight={500}
                    fontSize={"14.94px"}
                    color="#FF0000"
                  />
                </div>
                </div>
                <div>
                  <TextField
                    placeholder="Enter Country of Address"
                    variant="outlined"
                    size="small"
                    name="country"
                    value={values.country}
                    onChange={(e) => setFieldValue("country", e.target.value)}
                    error={touched.country && Boolean(errors.country)}
                    helperText={
                      errors.country && touched.country ? errors.country : ""
                    }
                    fullWidth
                  />
                </div>
              </div>

              {/* Remote Work Checkbox */}
              <div className="col mt-4" style={{ width: "fit-content" }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(values.allowsRemoteWork)}
                        onChange={(e) =>
                          setFieldValue("allowsRemoteWork", e.target.checked)
                        }
                        sx={{
                          color: "black",
                          "&.Mui-checked": {
                            color: "black",
                          },
                        }}
                      />
                    }
                    label="Allows remote work?"
                    sx={{ color: "black" }}
                  />
                </FormGroup>
              </div>

              <hr className="mt-5" />

              {/* Navigation Buttons */}
              <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 pt-4">
                <button
                  className="previous-btn w-100"
                  type="button"
                  onClick={() => {
                    // Ensure latest values are persisted before leaving the page
                    storeForm5DataToLocalStorage(values);
                    prevStep();
                  }}
                >
                  {isMobile
                    ? "Previous"
                    : "Previous - Recruiter Info & Contact Info"}
                </button>
                <button className="continue-btn w-100" type="submit">
                  {isMobile ? "Next" : "Next - Order Review"}
                </button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default Form5;
